import { inject, Injectable } from "@angular/core";
import {
  CampaignType,
  EntityIdType,
  EntityType,
  History,
  HistoryActionEnum,
  HistoryKey,
  SbCreative,
  SDStrategy,
  Strategy,
} from "@front/m19-api-client";
import { CampaignTypeWithTacos, StrategyEx } from "@front/m19-models";
import {
  AccountSelectionService,
  ActivityService,
  AuthService,
  EntityTypeFormats,
  PropertyFormatter,
  SbStrategyService,
  StrategyCache,
  TacosStrategiesService,
  TacosStrategy,
} from "@front/m19-services";
import { Option } from "@front/m19-ui";
import { API_DATE_FORMAT } from "@front/m19-utils";
import { DateTime } from "luxon";
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay, switchMap, zip } from "rxjs";
import { TranslocoService } from "@jsverse/transloco";
import { DataSetEventAnnotation } from "@front/m19-models/dataset";

@Injectable({
  providedIn: "root",
})
export class ActivityEventAnnotationsService {
  private readonly activityService = inject(ActivityService);
  private readonly authService = inject(AuthService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyCache = inject(StrategyCache);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly translocoService = inject(TranslocoService);

  public readonly allActivityEventTypesOptions: Option<ActivityEventType>[] = [];
  private readonly EntityTypePerCampaignType: Record<CampaignTypeWithTacos, EntityType[]> = {
    [CampaignType.SP]: [EntityType.Strategy, EntityType.Tactic, EntityType.StrategyGroup],
    [CampaignType.SB]: [EntityType.Strategy, EntityType.Tactic, EntityType.SbCreative],
    [CampaignType.SD]: [EntityType.Strategy, EntityType.Tactic, EntityType.AudienceTargeting],
    [CampaignType.DSP]: [EntityType.Strategy],
    TACOS: [EntityType.Strategy, EntityType.SbCreative, EntityType.AudienceTargeting, EntityType.TacosStrategyGroup],
  };
  public readonly activityEventTypesOptionsPerCampaignType: Map<CampaignTypeWithTacos, Option<ActivityEventType>[]> =
    new Map();

  public readonly activityEventsAnnotations$: Observable<DataSetEventAnnotation[]>;

  private readonly selectedActivityTypesSubject$: BehaviorSubject<ActivityEventType[]>;
  public readonly selectedActivityTypes$: Observable<ActivityEventType[]>;

  public readonly allStrategies$: Observable<StrategyEx[]>;
  private readonly selectedStrategiesSubject$: BehaviorSubject<StrategyEx[]>;
  public readonly selectedStrategies$: Observable<StrategyEx[]>;

  // used for dropdowns
  public readonly allStrategiesOptions$: Observable<Option<StrategyEx>[]>;
  public readonly selectedStrategiesOptions$: Observable<Option<StrategyEx>[]>;

  constructor() {
    const allActivityEventTypes: ActivityEventType[] = [];
    for (const entityType of [
      EntityType.Strategy,
      EntityType.Tactic,
      EntityType.SbCreative,
      EntityType.AudienceTargeting,
      EntityType.StrategyGroup,
      EntityType.TacosStrategyGroup,
    ]) {
      allActivityEventTypes.push({
        entityType,
        title: this.translocoService.translate(EntityTypeFormats[entityType] + "_creation"),
        match: (h) => h.entityType == entityType && h.action == HistoryActionEnum.create,
      });
      allActivityEventTypes.push({
        entityType,
        title: this.translocoService.translate(EntityTypeFormats[entityType] + "_deletion"),
        match: (h) => h.entityType == entityType && h.action == HistoryActionEnum.delete,
      });

      const formatter = this.activityService.getFormatter(entityType);
      if (!formatter) continue;

      for (const prop in formatter!.propertyFormatters) {
        const p: PropertyFormatter<any> = formatter!.propertyFormatters[prop]!;

        if (p?.formattedProperty) {
          allActivityEventTypes.push({
            entityType,
            title:
              this.translocoService.translate(EntityTypeFormats[entityType] + "_update") +
              ": " +
              this.translocoService.translate(p.formattedProperty),
            match: (h) => h.property == prop && h.action == HistoryActionEnum.update,
          });
        }
      }
    }
    this.allActivityEventTypesOptions = allActivityEventTypes.map((t) => ({ label: t.title, value: t }));
    for (const activityEventType of allActivityEventTypes) {
      const entityType = activityEventType.entityType;

      for (const [ct, entityTypes] of Object.entries(this.EntityTypePerCampaignType)) {
        if (entityTypes.includes(entityType)) {
          const campaignType = ct as CampaignTypeWithTacos;
          if (!this.activityEventTypesOptionsPerCampaignType.has(campaignType)) {
            this.activityEventTypesOptionsPerCampaignType.set(campaignType, []);
          }
          this.activityEventTypesOptionsPerCampaignType.get(campaignType)!.push({
            label: activityEventType.title,
            value: activityEventType,
          });
        }
      }
    }

    this.selectedActivityTypesSubject$ = new BehaviorSubject(allActivityEventTypes);
    this.selectedActivityTypes$ = this.selectedActivityTypesSubject$.asObservable();

    const strategyHistory$: Observable<History[]> = this.activityService
      .getHistory([
        { primaryType: EntityIdType.strategyId },
        { primaryType: EntityIdType.creativeId },
        { primaryType: EntityIdType.tacosStrategyGroupId },
      ])
      .pipe(shareReplay(1));

    this.allStrategies$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
      map((config) => Array.from(config.values())),
    );

    this.selectedStrategiesSubject$ = new BehaviorSubject<StrategyEx[]>([]);
    this.selectedStrategies$ = this.selectedStrategiesSubject$.asObservable();
    this.allStrategies$.subscribe((allStrategies) => this.selectedStrategiesSubject$.next(allStrategies));

    this.allStrategiesOptions$ = this.allStrategies$.pipe(
      map((all) => all.map((s: StrategyEx) => ({ label: s.name!, value: s }))),
    );

    this.selectedStrategiesOptions$ = this.selectedStrategies$.pipe(
      map((s) => s.map((strat) => ({ label: strat.name!, value: strat }))),
    );

    this.activityEventsAnnotations$ = this.buildStrategyActivityEventAnnotation(strategyHistory$);
  }

  public getTacosStrategyGroupActivityFilter(tacosStrategyGroup: TacosStrategy): HistoryKey[] {
    const filter: HistoryKey[] = [
      {
        primaryType: EntityIdType.tacosStrategyGroupId,
        primaryId: tacosStrategyGroup.tacosStrategyGroupId,
      },
    ];
    if (tacosStrategyGroup.sbStrategy) {
      filter.push(
        ...this.getStrategyActivityFilter(tacosStrategyGroup.sbStrategy, tacosStrategyGroup.sbCreatives ?? []),
      );
    }
    if (tacosStrategyGroup.sdStrategy) {
      filter.push(...this.getStrategyActivityFilter(tacosStrategyGroup.sdStrategy, []));
    }
    return filter;
  }

  public getStrategyActivityFilter(strategy: Strategy, creatives: SbCreative[]): HistoryKey[] {
    const filter = [
      {
        primaryType: EntityIdType.strategyId,
        primaryId: strategy.strategyId,
      },
    ];
    if (strategy.campaignType == CampaignType.SB) {
      filter.push(
        ...creatives.map((c) => ({
          primaryType: EntityIdType.creativeId,
          primaryId: c.creativeId,
        })),
      );
    }
    if (strategy.campaignType == CampaignType.SD) {
      filter.push(
        ...(strategy as SDStrategy).audienceTargetings.map((a) => ({
          primaryType: EntityIdType.audienceTargetId,
          primaryId: a.audienceTargetId!,
        })),
      );
    }
    return filter;
  }

  public getStrategiesActivityEventAnnotation(
    strategies?: Strategy[],
    creativesPerStrategy: Map<number, SbCreative[]> = new Map(),
    tacosStrategies?: TacosStrategy[],
  ) {
    if (!strategies && !tacosStrategies) {
      return this.buildStrategyActivityEventAnnotation(this.activityService.getHistory());
    }

    if (tacosStrategies && tacosStrategies.length === 0 && strategies && strategies.length === 0) {
      return of([]);
    }

    let activityFilters: HistoryKey[] = [];
    if (tacosStrategies) {
      activityFilters = tacosStrategies.flatMap((ts) => this.getTacosStrategyGroupActivityFilter(ts));
    } else {
      activityFilters = strategies!.flatMap((strategy) =>
        this.getStrategyActivityFilter(strategy, creativesPerStrategy.get(strategy.strategyId!) ?? []),
      );
    }

    return this.buildStrategyActivityEventAnnotation(this.activityService.getHistory(activityFilters));
  }

  public getStrategyActivityEventAnnotation(strategy: StrategyEx) {
    return this.getStrategiesActivityEventAnnotation([strategy]);
  }

  private buildStrategyActivityEventAnnotation(history$: Observable<History[]>): Observable<DataSetEventAnnotation[]> {
    return combineLatest([
      history$,
      this.selectedActivityTypes$,
      this.selectedStrategies$,
      this.authService.loggedUser$,
      this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) =>
          combineLatest([
            this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace),
            this.sbStrategyService.getSbCreativesIndex(am.accountId, am.marketplace),
            this.tacosStrategiesService.getTacosStrategyIndex(am.accountId, am.marketplace),
          ]),
        ),
      ),
    ]).pipe(
      switchMap(
        ([
          history,
          selectedActivityTypes,
          selectedStrategies,
          user,
          [strategyIndex, sbCreativeIndex, tacosStrategyIndex],
        ]) => {
          const eventsByDate = new Map<string, Observable<string>[]>();
          for (const historyEvent of history) {
            const isEventTypeSelected = selectedActivityTypes.some((eventType) => eventType.match(historyEvent));
            if (!isEventTypeSelected) {
              continue;
            }

            let strategyId: number | undefined;
            if (historyEvent.entityType == EntityType.SbCreative) {
              strategyId = sbCreativeIndex.get(historyEvent.primaryId!)?.strategyId;
            } else if (historyEvent.entityType == EntityType.TacosStrategyGroup) {
              strategyId = tacosStrategyIndex.get(historyEvent.primaryId!)?.spStrategyId;
            } else {
              strategyId = historyEvent.primaryId;
            }

            if (
              historyEvent.entityType !== EntityType.TacosStrategyGroup &&
              !selectedStrategies.some((s) => s.strategyId === strategyId)
            ) {
              continue;
            }

            const label$ = this.getEventAnnotationLabel(historyEvent, strategyIndex.get(strategyId!), user.locale);

            const dateStr = DateTime.fromISO(historyEvent.timestamp, { zone: "utc" })
              .startOf("day")
              .toFormat(API_DATE_FORMAT);

            if (eventsByDate.has(dateStr)) {
              eventsByDate.get(dateStr)!.push(label$);
            } else {
              eventsByDate.set(dateStr, [label$]);
            }
          }
          const allDataSetAnnotations$: Observable<DataSetEventAnnotation>[] = [];
          for (const [date, labelsForDate$] of eventsByDate.entries()) {
            const annotation$: Observable<DataSetEventAnnotation> = zip(labelsForDate$).pipe(
              map((labels) => ({
                color: "#f68c8e",
                label: labels,
                date: new Date(date),
              })),
            );

            allDataSetAnnotations$.push(annotation$);
          }
          return allDataSetAnnotations$.length ? zip(allDataSetAnnotations$) : of([]);
        },
      ),
    );
  }

  private getEventAnnotationLabel(
    historyEvent: History,
    strategy: StrategyEx | undefined,
    locale: string,
  ): Observable<string> {
    let label$: Observable<string>;
    if (historyEvent.action == HistoryActionEnum.create) {
      label$ = this.activityService
        .formatHistoryValue(historyEvent, "newValue", locale)
        .pipe(
          map(
            (newVal) =>
              this.translocoService.translate(EntityTypeFormats[historyEvent.entityType]) +
              ` ${this.translocoService.translate("activity-service.creation")} - ${newVal.value}`,
          ),
        );
    } else if (historyEvent.action == HistoryActionEnum.delete) {
      label$ = this.activityService
        .formatHistoryValue(historyEvent, "oldValue", locale)
        .pipe(
          map(
            (newVal) =>
              this.translocoService.translate(EntityTypeFormats[historyEvent.entityType]) +
              ` ${this.translocoService.translate("activity-service.deletion")} - ${newVal.value}`,
          ),
        );
    } else {
      const strategyName = strategy?.name ?? "Unknown";

      label$ = zip(
        this.activityService.formatHistoryValue(historyEvent, "oldValue", locale),
        this.activityService.formatHistoryValue(historyEvent, "newValue", locale),
      ).pipe(
        map(([oldVal, newVal]) => {
          return (
            `${this.translocoService.translate(EntityTypeFormats[historyEvent.entityType] + "_update")} in "${strategyName}" ` +
            `${this.activityService.formatHistoryProperty(historyEvent)} changes` +
            `${historyEvent.oldValue ? " from " + oldVal.value : ""} to ${newVal.value}`
          );
        }),
      );
    }
    return label$;
  }

  public selectActivityEventAnnotationTypes(selectedActivityTypes: ActivityEventType[]) {
    this.selectedActivityTypesSubject$.next(selectedActivityTypes);
  }

  public selectStrategies(strategies: StrategyEx[]) {
    this.selectedStrategiesSubject$.next(strategies);
  }
}

export type ActivityEventType = {
  entityType: EntityType;
  title: string;
  match: (h: History) => boolean;
};
